import { useCallback, useEffect, useRef, useState } from 'react';
import FilterDropdown from '../components/FilterDropdown';
import { getMainEvents, importMainEvents } from '../services/mainEventService';

const PAGE_SIZE = 50;
const MAX_FILE_BYTES = 5 * 1024 * 1024;

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const MONTH_OPTIONS = [
  { value: '', label: 'All Months' },
  ...MONTH_NAMES.map((name, index) => ({ value: String(index + 1), label: name })),
];

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Status' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

const uploadIcon = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

function formatNumber(value) {
  if (value === null || value === undefined || value === '') return '—';
  const num = Number(value);
  return Number.isNaN(num) ? String(value) : num.toLocaleString('en-US');
}

function getPageItems(current, total) {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const items = [1];

  if (current > 3) items.push('…');

  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);

  for (let pageNum = start; pageNum <= end; pageNum += 1) {
    items.push(pageNum);
  }

  if (current < total - 2) items.push('…');

  items.push(total);
  return items;
}

function eventMonthLabel(event) {
  const monthName = event.monthName || MONTH_NAMES[(Number(event.month) || 0) - 1] || '';
  return [monthName, event.year].filter(Boolean).join(' ');
}

function ImportEventsModal({ open, onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [mode, setMode] = useState('append');
  const [year, setYear] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [rowErrors, setRowErrors] = useState([]);
  const [preview, setPreview] = useState(null);

  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setFile(null);
    setMode('append');
    setYear('');
    setSubmitting(false);
    setError('');
    setRowErrors([]);
    setPreview(null);
  }, [open]);

  if (!open) return null;

  function resetResults() {
    setError('');
    setRowErrors([]);
    setPreview(null);
  }

  function handleFileChange(e) {
    const nextFile = e.target.files?.[0] || null;
    resetResults();

    if (!nextFile) {
      setFile(null);
      return;
    }
    if (!nextFile.name.toLowerCase().endsWith('.xlsx')) {
      setFile(null);
      setError('Please choose an .xlsx file.');
      e.target.value = '';
      return;
    }
    if (nextFile.size > MAX_FILE_BYTES) {
      setFile(null);
      setError('The file must be 5 MB or smaller.');
      e.target.value = '';
      return;
    }
    setFile(nextFile);
  }

  async function submit(dryRun) {
    if (!file) {
      setError('Please choose an .xlsx file.');
      return;
    }

    setSubmitting(true);
    resetResults();

    try {
      const response = await importMainEvents({
        file,
        mode,
        year: year ? Number(year) : undefined,
        dryRun,
      });

      if (dryRun) {
        setPreview(response.data || {});
        return;
      }

      onImported?.(response);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to import events.');
      setRowErrors(Array.isArray(err.data?.errors) ? err.data.errors : []);
    } finally {
      setSubmitting(false);
    }
  }

  const previewEvents = Array.isArray(preview?.preview) ? preview.preview : [];

  return (
    <div className="modal-overlay" onClick={submitting ? undefined : onClose}>
      <div
        className="add-offer-modal main-events-import-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="main-events-import-title"
      >
        <div className="add-offer-modal-header">
          <div>
            <h2 id="main-events-import-title">Import Main Events</h2>
            <p>Upload the Global Events Calendar spreadsheet (.xlsx, max 5 MB).</p>
          </div>
          <button
            type="button"
            className="add-restaurant-close"
            onClick={onClose}
            disabled={submitting}
            aria-label="Close"
          >
            &#x2715;
          </button>
        </div>

        <form
          className="add-offer-form"
          onSubmit={(e) => {
            e.preventDefault();
            submit(false);
          }}
        >
          <div className="add-restaurant-field">
            <span>Spreadsheet</span>
            <button
              type="button"
              className="main-events-file-picker"
              onClick={() => fileInputRef.current?.click()}
              disabled={submitting}
            >
              {uploadIcon}
              <span className="main-events-file-name">{file ? file.name : 'Choose .xlsx file'}</span>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={handleFileChange}
              hidden
            />
            <a
              className="main-events-sample-link"
              href={`${process.env.PUBLIC_URL}/sample.xlsx`}
              download="main-events-sample.xlsx"
            >
              Download sample sheet
            </a>
          </div>

          <div className="add-restaurant-field">
            <span>Mode</span>
            <div className="add-offer-status-toggle" role="group" aria-label="Import mode">
              <button
                type="button"
                className={`add-offer-status-btn ${mode === 'append' ? 'is-active' : ''}`}
                onClick={() => {
                  setMode('append');
                  resetResults();
                }}
              >
                Append
              </button>
              <button
                type="button"
                className={`add-offer-status-btn ${mode === 'replace' ? 'is-active' : ''}`}
                onClick={() => {
                  setMode('replace');
                  resetResults();
                }}
              >
                Replace
              </button>
            </div>
            <p className={`main-events-mode-hint ${mode === 'replace' ? 'is-warning' : ''}`}>
              {mode === 'replace'
                ? 'Deletes ALL existing main events, then adds the events from this file.'
                : 'Keeps existing events. Events with the same name, city, year and month are updated; the rest are added.'}
            </p>
          </div>

          <label className="add-restaurant-field">
            <span>Year (optional)</span>
            <input
              type="number"
              placeholder="Only needed if the sheet title has no year"
              value={year}
              min={2000}
              max={2100}
              onChange={(e) => {
                setYear(e.target.value);
                resetResults();
              }}
            />
          </label>

          {error && (
            <div className="add-restaurant-error">
              {error}
              {rowErrors.length > 0 && (
                <ul className="main-events-row-errors">
                  {rowErrors.map((rowError, index) => (
                    <li key={`${rowError.row}-${index}`}>
                      {rowError.row ? <strong>Row {rowError.row}: </strong> : null}
                      {rowError.message}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {preview && (
            <div className="main-events-preview">
              <div className="main-events-preview-summary">
                <strong>Preview — nothing saved yet.</strong>
                <span>
                  {formatNumber(preview.eventsInFile ?? previewEvents.length)} events found
                  {preview.year ? ` for ${preview.year}` : ''}
                  {preview.sheet ? ` in sheet "${preview.sheet}"` : ''}.
                </span>
              </div>
              {previewEvents.length > 0 && (
                <div className="main-events-preview-list">
                  <table className="users-table">
                    <thead>
                      <tr>
                        <th>Event</th>
                        <th>City</th>
                        <th>Date</th>
                        <th>Category</th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewEvents.map((event, index) => (
                        <tr key={`${event.name}-${event.city}-${index}`}>
                          <td>{event.name || '—'}</td>
                          <td>{[event.city, event.country].filter(Boolean).join(', ') || '—'}</td>
                          <td>{event.dateLabel || eventMonthLabel(event) || '—'}</td>
                          <td>{event.category || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          <div className="add-restaurant-footer main-events-import-footer">
            <button
              type="button"
              className="modal-btn modal-btn-cancel"
              onClick={() => submit(true)}
              disabled={submitting || !file}
            >
              {submitting ? 'Working...' : 'Preview'}
            </button>
            <button type="submit" className="add-restaurant-save" disabled={submitting || !file}>
              {submitting ? 'Importing...' : mode === 'replace' ? 'Replace Events' : 'Import Events'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function MainEvents() {
  const [events, setEvents] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [yearFilter, setYearFilter] = useState('');
  const [monthFilter, setMonthFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [yearOptions, setYearOptions] = useState([{ value: '', label: 'All Years' }]);
  const [showImport, setShowImport] = useState(false);
  const [importMessage, setImportMessage] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  const scrollRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    let cancelled = false;

    async function loadEvents() {
      setLoading(true);
      setError('');

      try {
        const response = await getMainEvents({
          page,
          limit: PAGE_SIZE,
          search: debouncedSearch || undefined,
          year: yearFilter || undefined,
          month: monthFilter || undefined,
          status: statusFilter,
        });

        if (cancelled) return;

        const data = response.data || {};
        setEvents(Array.isArray(data.events) ? data.events : []);
        setSummary(data.summary || null);
        setTotalPages(data.pagination?.totalPages || 1);

        const years = Array.isArray(data.summary?.years) ? data.summary.years : [];
        // Only refresh the year list while it isn't narrowed by the year filter itself.
        if (!yearFilter && years.length > 0) {
          setYearOptions([
            { value: '', label: 'All Years' },
            ...years.map((item) => ({ value: String(item.year), label: String(item.year) })),
          ]);
        }
      } catch (err) {
        if (cancelled) return;
        setError(err.message || 'Failed to fetch main events.');
        setEvents([]);
        setTotalPages(1);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadEvents();
    return () => {
      cancelled = true;
    };
  }, [page, debouncedSearch, yearFilter, monthFilter, statusFilter, reloadKey]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [page]);

  const handleImported = useCallback((response) => {
    const data = response?.data || {};
    const fallback = data.mode === 'replace'
      ? `Replaced: ${formatNumber(data.deleted)} old events deleted, ${formatNumber(data.inserted)} added.`
      : `Imported: ${formatNumber(data.inserted)} added, ${formatNumber(data.updated)} updated.`;
    setImportMessage(response?.message || fallback);
    setPage(1);
    setReloadKey((key) => key + 1);
  }, []);

  function goToPage(nextPage) {
    if (nextPage < 1 || nextPage > totalPages || nextPage === page || loading) return;
    setPage(nextPage);
  }

  const pageItems = getPageItems(page, totalPages);

  const summaryCards = [
    { label: 'Total Events', value: summary?.total, sub: 'Matching current filters' },
    { label: 'Active', value: summary?.active, sub: 'Shown in the app' },
    { label: 'Inactive', value: summary?.inactive, sub: 'Hidden from the app' },
    {
      label: 'Years',
      value: summary?.years?.length,
      sub: (summary?.years || []).map((item) => `${item.year}: ${formatNumber(item.count)}`).join(' · ') || '—',
    },
  ];

  return (
    <div className="payments-page main-bg-top">
      <div className="stats-grid petal-txn-summary main-events-summary">
        {summaryCards.map((card) => (
          <div className="stat-card" key={card.label}>
            <div className="stat-info">
              <span className="stat-label">{card.label}</span>
              <span className="stat-value">{formatNumber(card.value)}</span>
              <span className="stat-sub">{card.sub}</span>
            </div>
          </div>
        ))}
      </div>

      {importMessage && (
        <div className="main-events-import-success" role="status">
          <span>{importMessage}</span>
          <button type="button" onClick={() => setImportMessage('')} aria-label="Dismiss">&#x2715;</button>
        </div>
      )}

      <div className="users-header">
        <div className="users-filters petal-txn-filters">
          <div className="users-search">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              placeholder="Search by name, city or country..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <FilterDropdown
            ariaLabel="Filter by year"
            value={yearFilter}
            options={yearOptions}
            onChange={(next) => {
              setYearFilter(next);
              setPage(1);
            }}
          />
          <FilterDropdown
            ariaLabel="Filter by month"
            value={monthFilter}
            options={MONTH_OPTIONS}
            onChange={(next) => {
              setMonthFilter(next);
              setPage(1);
            }}
          />
          <FilterDropdown
            ariaLabel="Filter by status"
            value={statusFilter}
            options={STATUS_OPTIONS}
            onChange={(next) => {
              setStatusFilter(next);
              setPage(1);
            }}
          />
        </div>
        <button type="button" className="offers-add-btn" onClick={() => setShowImport(true)}>
          {uploadIcon}
          Import Events
        </button>
      </div>

      <div className="payments-table-scroll" ref={scrollRef}>
        <div className="users-table-wrap">
          <table className="users-table payments-table">
            <thead>
              <tr>
                <th>Event</th>
                <th>Location</th>
                <th>Date</th>
                <th>Category</th>
                <th>Exclusivity</th>
                <th>Est. Attendance</th>
                <th>Approx. Price</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" className="users-empty">Loading main events...</td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan="8" className="users-empty">{error}</td>
                </tr>
              ) : events.length === 0 ? (
                <tr>
                  <td colSpan="8" className="users-empty">No main events found.</td>
                </tr>
              ) : (
                events.map((event) => {
                  const name = event.name || '—';
                  return (
                    <tr key={event.id}>
                      <td>
                        <div className="user-cell">
                          <div className="user-cell-avatar">
                            {event.imageUrl ? (
                              <img
                                src={event.imageUrl}
                                alt={name}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.parentElement.textContent = name !== '—' ? name.charAt(0).toUpperCase() : '?';
                                }}
                              />
                            ) : (
                              <span className="avatar-placeholder">
                                {name !== '—' ? name.charAt(0).toUpperCase() : '?'}
                              </span>
                            )}
                          </div>
                          <div className="user-cell-info">
                            <span className="user-cell-name">{name}</span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="datetime-cell">
                          <span className="datetime-date">{event.city || '—'}</span>
                          <span className="datetime-time">{event.country || ''}</span>
                        </div>
                      </td>
                      <td className="utm-nowrap-cell">
                        <div className="datetime-cell">
                          <span className="datetime-date">{event.dateLabel || '—'}</span>
                          <span className="datetime-time">{eventMonthLabel(event)}</span>
                        </div>
                      </td>
                      <td>{event.category || '—'}</td>
                      <td>{event.exclusivity || '—'}</td>
                      <td>{event.estAttendance || '—'}</td>
                      <td>{event.priceLabel || '—'}</td>
                      <td>
                        <span className={`status-badge ${event.isActive ? 'status-active' : 'status-inactive'}`}>
                          {event.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {!loading && !error && totalPages > 1 && (
        <div className="payments-pagination" aria-label="Pagination">
          <button
            type="button"
            className="pagination-page-btn"
            disabled={page <= 1}
            onClick={() => goToPage(page - 1)}
            aria-label="Previous page"
          >
            ‹
          </button>

          {pageItems.map((item, index) =>
            item === '…' ? (
              <span key={`ellipsis-${index}`} className="pagination-ellipsis">
                …
              </span>
            ) : (
              <button
                key={item}
                type="button"
                className={`pagination-page-btn ${page === item ? 'active' : ''}`}
                onClick={() => goToPage(item)}
                aria-current={page === item ? 'page' : undefined}
              >
                {item}
              </button>
            )
          )}

          <button
            type="button"
            className="pagination-page-btn"
            disabled={page >= totalPages}
            onClick={() => goToPage(page + 1)}
            aria-label="Next page"
          >
            ›
          </button>
        </div>
      )}

      <ImportEventsModal open={showImport} onClose={() => setShowImport(false)} onImported={handleImported} />
    </div>
  );
}
